
import { ProjectState, Task, Session, TaskType, TimeUnit, TaskStatus } from '../types';

/**
 * Downloads the current project state as a JSON file.
 */
export const exportProjectToJSON = (project: ProjectState) => {
  console.log('Exporting project to JSON...', project.name);
  const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(project, null, 2));
  const downloadAnchorNode = document.createElement('a');
  downloadAnchorNode.setAttribute("href", dataStr);
  downloadAnchorNode.setAttribute("download", `project-${project.name.replace(/\s+/g, '_').toLowerCase()}.json`);
  document.body.appendChild(downloadAnchorNode);
  downloadAnchorNode.click();
  downloadAnchorNode.remove();
};

/**
 * Exports the project to an MS Project XML (MSPDI) file.
 */
export const exportProjectToMSP = (project: ProjectState) => {
  console.log('Exporting project to MS Project XML...', project.name);
  const startDate = new Date(project.startDate);
  
  // Helper to format date as YYYY-MM-DDTHH:MM:SS
  const formatDate = (date: Date) => {
    return date.toISOString().split('.')[0];
  };

  // Helper to add hours to a date
  const addHours = (date: Date, hours: number) => {
    return new Date(date.getTime() + hours * 60 * 60 * 1000);
  };

  // Map task IDs to integer UIDs for MS Project
  const taskUidMap = new Map<string, number>();
  project.tasks.forEach((t, index) => {
    taskUidMap.set(t.id, index + 1);
  });

  let xml = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n';
  xml += '<Project xmlns="http://schemas.microsoft.com/project">\n';
  xml += `  <Name>${escapeXml(project.name)}</Name>\n`;
  xml += `  <Title>${escapeXml(project.name)}</Title>\n`;
  xml += `  <StartDate>${formatDate(startDate)}</StartDate>\n`;
  xml += '  <Tasks>\n';

  project.tasks.forEach((task) => {
    const uid = taskUidMap.get(task.id);
    const start = addHours(startDate, task.earlyStart || 0);
    const finish = addHours(startDate, task.earlyFinish || 0);
    
    // Duration in MS Project Format: PT<hours>H<minutes>M<seconds>S
    // Assuming task.duration is in the unit specified. We need to normalize to hours for simplicity or handle units.
    // The app seems to normalize everything to hours for CPM, but stores raw duration/unit.
    // Let's use the calculated duration (earlyFinish - earlyStart) which is in hours.
    const durationHours = (task.earlyFinish || 0) - (task.earlyStart || 0);
    const durationStr = `PT${Math.floor(durationHours)}H${Math.round((durationHours % 1) * 60)}M0S`;

    xml += '    <Task>\n';
    xml += `      <UID>${uid}</UID>\n`;
    xml += `      <ID>${uid}</ID>\n`;
    xml += `      <Name>${escapeXml(task.name)}</Name>\n`;
    xml += `      <Active>1</Active>\n`;
    xml += `      <Manual>0</Manual>\n`; // Auto-scheduled
    xml += `      <Type>0</Type>\n`; // Fixed Units
    xml += `      <Start>${formatDate(start)}</Start>\n`;
    xml += `      <Finish>${formatDate(finish)}</Finish>\n`;
    xml += `      <Duration>${durationStr}</Duration>\n`;
    xml += `      <Critical>${task.isCritical ? 1 : 0}</Critical>\n`;
    
    if (task.dependencies && task.dependencies.length > 0) {
      task.dependencies.forEach(depId => {
        const depUid = taskUidMap.get(depId);
        if (depUid) {
          xml += '      <PredecessorLink>\n';
          xml += `        <PredecessorUID>${depUid}</PredecessorUID>\n`;
          xml += `        <Type>1</Type>\n`; // Finish-to-Start
          xml += `        <CrossProject>0</CrossProject>\n`;
          xml += '      </PredecessorLink>\n';
        }
      });
    }

    xml += '    </Task>\n';
  });

  xml += '  </Tasks>\n';
  xml += '</Project>';

  const blob = new Blob([xml], { type: 'application/xml' });
  const url = URL.createObjectURL(blob);
  const downloadAnchorNode = document.createElement('a');
  downloadAnchorNode.setAttribute("href", url);
  downloadAnchorNode.setAttribute("download", `project-${project.name.replace(/\s+/g, '_').toLowerCase()}.xml`);
  document.body.appendChild(downloadAnchorNode);
  downloadAnchorNode.click();
  downloadAnchorNode.remove();
  URL.revokeObjectURL(url);
};

const escapeXml = (unsafe: string) => {
  return unsafe.replace(/[<>&'"]/g, (c) => {
    switch (c) {
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '&': return '&amp;';
      case '\'': return '&apos;';
      case '"': return '&quot;';
      default: return c;
    }
  });
};

/**
 * Parses and normalizes incoming JSON.
 */
export const parseImportedJSON = async (file: File): Promise<ProjectState> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      try {
        const json = JSON.parse(event.target?.result as string);
        
        if (json.project && json.project.id && Array.isArray(json.tasks)) {
           resolve(mapExternalFormat(json));
        } 
        else if (Array.isArray(json.tasks) && Array.isArray(json.sessions)) {
           resolve(json as ProjectState);
        }
        else {
           reject(new Error("Unrecognized JSON format. Missing 'tasks' or 'sessions'."));
        }
      } catch (e) {
        reject(e);
      }
    };
    
    reader.onerror = (e) => reject(e);
    reader.readAsText(file);
  });
};

const mapExternalFormat = (external: any): ProjectState => {
  const project: ProjectState = {
    id: external.project?.id || 'imported-proj',
    name: external.project?.name || 'Imported Project',
    description: external.project?.description || '',
    startDate: new Date().toISOString(),
    sessions: [],
    tasks: []
  };

  if (Array.isArray(external.sessions)) {
    project.sessions = external.sessions.map((s: any) => ({
      id: s.id,
      name: s.name,
      description: s.description
    }));
  }

  if (Array.isArray(external.tasks)) {
    project.tasks = external.tasks.map((t: any) => {
        let duration = 1;
        let unit = TimeUnit.HOUR;
        
        if (typeof t.duration === 'object') {
            duration = t.duration.value;
            const u = t.duration.unit;
            if (u === '15m') unit = TimeUnit.QUARTER;
            else if (u === '1h') unit = TimeUnit.HOUR;
            else if (u === '1d') unit = TimeUnit.DAY;
            else if (u === '1w') unit = TimeUnit.WEEK;
        } else if (typeof t.duration === 'number') {
            duration = t.duration;
        }

        return {
            id: t.id,
            name: t.name,
            description: t.description,
            type: TaskType.STANDARD,
            duration: duration,
            unit: unit,
            sessionId: t.sessionId,
            dependencies: t.dependencies || [],
            status: (t.status as TaskStatus) || TaskStatus.TODO,
            priority: t.priority,
            notes: t.notes
        };
    });
  }

  return project;
};

/**
 * Generates a cleaner text summary using specific arrow syntax.
 * ==> Session
 *     => Critical Task
 *     -> Standard Task
 */
export const generateTextSummary = (project: ProjectState): string => {
  const lines: string[] = [];
  const totalDuration = Math.max(0, ...project.tasks.map(t => t.earlyFinish || 0)).toFixed(1);

  // Root Node (Project)
  lines.push(`root: ${project.name} (${totalDuration}h)`);
  if (project.description) {
      lines.push(` # ${project.description}`);
  }
  lines.push(''); // Spacer

  // 1. Group tasks by session
  const tasksBySession: Record<string, Task[]> = {};
  project.sessions.forEach(s => tasksBySession[s.id] = []);
  tasksBySession['ungrouped'] = [];

  project.tasks.forEach(t => {
    if (t.sessionId && tasksBySession[t.sessionId]) {
      tasksBySession[t.sessionId].push(t);
    } else {
      tasksBySession['ungrouped'].push(t);
    }
  });

  // 2. Prepare Sessions List
  const sessions = [
    ...project.sessions.map(s => ({ id: s.id, name: s.name, description: s.description, tasks: tasksBySession[s.id] })),
  ];
  if (tasksBySession['ungrouped'].length > 0) {
    sessions.push({ id: 'ungrouped', name: 'General / Backlog', description: undefined, tasks: tasksBySession['ungrouped'] });
  }

  // --- Recursive Tree Renderer ---
  const renderTaskNode = (
    task: Task, 
    sessionTasks: Task[], 
    indent: string, 
    rendered: Set<string>
  ) => {
    rendered.add(task.id);
    
    // Determine Symbol
    // => Critical
    // -> Standard
    const symbol = task.isCritical ? '=>' : '->';
    const status = task.status === TaskStatus.COMPLETED ? '[x]' : '[ ]';
    
    // Construct Label with separator (e.g., "2 x 1h")
    const label = `${symbol} ${status} ${task.name} (${task.duration} x ${task.unit})`;
    lines.push(`${indent}${label}`);

    // Metadata (Dependencies / Notes) - Indented further
    const childIndent = indent + '    '; // 4 spaces indentation for children

    if (task.dependencies.length > 0) {
        // Filter deps that are NOT rendered yet to avoid confusing loops in display
        // or just list them for reference
        const depNames = project.tasks
           .filter(d => task.dependencies.includes(d.id))
           .map(d => d.name)
           .join(', ');
        // We only show explicit "Wait for" if it helps context, but strict hierarchy usually implies it.
        // However, user requested "Wait for" logic to be visible.
        lines.push(`${childIndent}Wait for: ${depNames}`);
    }
    
    if (task.notes) {
        lines.push(`${childIndent}Note: ${task.notes}`);
    }

    // Render Children (Tasks that depend on this one, within this session)
    const childrenTasks = sessionTasks
        .filter(t => t.dependencies.includes(task.id) && !rendered.has(t.id))
        .sort((a, b) => (a.earlyStart || 0) - (b.earlyStart || 0));

    childrenTasks.forEach(child => {
        renderTaskNode(child, sessionTasks, childIndent, rendered);
    });
  };

  // 3. Render Sessions
  sessions.forEach((session) => {
    // Session Header: ==> [ ] Session Name
    lines.push(`==> [ ] ${session.name}`);
    if (session.description) {
        lines.push(`      # ${session.description}`);
    }

    const renderedIds = new Set<string>();
    const sortedTasks = [...session.tasks].sort((a, b) => (a.earlyStart || 0) - (b.earlyStart || 0));
    
    // Find Roots (Visual Roots)
    const roots: Task[] = [];
    const simulatedRendered = new Set<string>();
    
    sortedTasks.forEach(task => {
        if (!simulatedRendered.has(task.id)) {
            roots.push(task);
            const stack = [task];
            while(stack.length > 0) {
                const curr = stack.pop()!;
                simulatedRendered.add(curr.id);
                const kids = session.tasks.filter(t => t.dependencies.includes(curr.id) && !simulatedRendered.has(t.id));
                stack.push(...kids);
            }
        }
    });

    // Render Roots
    roots.forEach((root) => {
        renderTaskNode(root, session.tasks, '    ', renderedIds);
    });
    
    lines.push(''); // Spacer between sessions
  });

  return lines.join('\n');
};
